import { Button } from '@/components/ui/button';
import {
  Eye,
  EyeOff,
  Maximize,
  Minimize,
  Minus,
  Monitor,
  Plus,
  RotateCcw,
  Settings,
  Smartphone,
  Tablet
} from 'lucide-react';
import React, { useCallback } from 'react';

export type ResponsiveMode = 'desktop' | 'tablet' | 'mobile' | 'custom';
export type ZoomLevel = 50 | 75 | 100 | 125 | 150 | 200;

interface PreviewToolbarProps {
  zoomLevel: ZoomLevel;
  onZoomChange: (zoom: ZoomLevel) => void;
  responsiveMode: ResponsiveMode;
  onResponsiveModeChange: (mode: ResponsiveMode) => void;
  customWidth: number;
  onCustomWidthChange: (width: number) => void;
  isFullscreen: boolean;
  onFullscreenToggle: () => void;
  onRefresh: () => void;
  showEditor: boolean;
  onToggleEditor: () => void;
}

const ZOOM_LEVELS: ZoomLevel[] = [50, 75, 100, 125, 150, 200];

const RESPONSIVE_MODES = [
  { mode: 'desktop' as const, label: 'Desktop', width: 1200, icon: Monitor },
  { mode: 'tablet' as const, label: 'Tablet', width: 768, icon: Tablet },
  { mode: 'mobile' as const, label: 'Mobile', width: 375, icon: Smartphone },
  { mode: 'custom' as const, label: 'Custom', width: 0, icon: Settings },
];

export const PreviewToolbar: React.FC<PreviewToolbarProps> = ({
  zoomLevel,
  onZoomChange,
  responsiveMode,
  onResponsiveModeChange,
  customWidth,
  onCustomWidthChange,
  isFullscreen,
  onFullscreenToggle,
  onRefresh,
  showEditor,
  onToggleEditor,
}) => {
  const handleZoomOut = useCallback(() => {
    const currentIndex = ZOOM_LEVELS.indexOf(zoomLevel);
    if (currentIndex > 0) {
      onZoomChange(ZOOM_LEVELS[currentIndex - 1]);
    }
  }, [zoomLevel, onZoomChange]);

  const handleZoomIn = useCallback(() => {
    const currentIndex = ZOOM_LEVELS.indexOf(zoomLevel);
    if (currentIndex < ZOOM_LEVELS.length - 1) {
      onZoomChange(ZOOM_LEVELS[currentIndex + 1]);
    }
  }, [zoomLevel, onZoomChange]);

  const handleCustomWidthChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 200) {
      onCustomWidthChange(value);
    }
  }, [onCustomWidthChange]);

  const canZoomOut = ZOOM_LEVELS.indexOf(zoomLevel) > 0;
  const canZoomIn = ZOOM_LEVELS.indexOf(zoomLevel) < ZOOM_LEVELS.length - 1;

  return (
    <div className="flex items-center justify-between gap-4 p-3 bg-card border-b border-border">
      {/* Zoom Controls */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Zoom:</span>
        <div className="flex items-center border border-border rounded-md overflow-hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={!canZoomOut}
            className="h-8 px-2 rounded-none border-r border-border"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="px-3 py-1 text-sm font-mono bg-muted min-w-[60px] text-center">
            {zoomLevel}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={!canZoomIn}
            className="h-8 px-2 rounded-none border-l border-border"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Responsive Mode Controls */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Device:</span>
        <div className="flex items-center border border-border rounded-md overflow-hidden">
          {RESPONSIVE_MODES.map(({ mode, label, icon: Icon }) => (
            <button
              key={mode}
              className={`
                px-3 py-1 text-sm transition-colors border-r border-border last:border-r-0 flex items-center gap-1
                ${responsiveMode === mode
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-muted text-foreground'
                }
              `}
              onClick={() => onResponsiveModeChange(mode)}
              title={label}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {responsiveMode === 'custom' && (
          <div className="flex items-center gap-1 ml-2">
            <input
              type="number"
              value={customWidth}
              onChange={handleCustomWidthChange}
              min="200"
              max="2000"
              step="50"
              className="w-20 px-2 py-1 text-sm border border-border rounded bg-background"
            />
            <span className="text-xs text-muted-foreground">px</span>
          </div>
        )}
      </div>

      {/* Action Controls */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          className="gap-1"
        >
          <RotateCcw className="h-4 w-4" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleEditor}
          className="gap-1"
        >
          {showEditor ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          <span className="hidden sm:inline">{showEditor ? 'Hide Editor' : 'Show Editor'}</span>
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onFullscreenToggle}
          className="gap-1"
        >
          {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
          <span className="hidden sm:inline">{isFullscreen ? 'Exit' : 'Fullscreen'}</span>
        </Button>
      </div>
    </div>
  );
}; 